/**
 * Каталог моделей и цены: разбор ответа API и вся арифметика таблицы.
 *
 * Отдельным модулем, а не внутри компонента, по той же причине, что и
 * `proxy-list.ts`: одни и те же данные читает серверная страница (чтобы
 * отдать первую выдачу в HTML), route handler и таблица в браузере.
 *
 * Здесь же вся логика поиска, сортировки и листания. У прокси этим
 * занимается API, а здесь не может: `GET /llm/models` отдаёт весь
 * каталог одним куском без параметров фильтрации (контракт в
 * AI-Tech-API, `chat_handler.go`, ручка Models). Значит, считаем в
 * браузере — и именно поэтому арифметика вынесена в чистые функции,
 * которые проверяются тестом, а не глазами по экрану.
 */

/** Цена за токен в валюте клиента. Строки, а не числа: так отдаёт API
 *  (доли вида 0.00000025 теряют точность при проходе через float). */
export interface CenaModeli {
  prompt: string;
  completion: string;
  currency: string;
  /** Фиксированная цена за запрос (ai-saga-pro), как pricing.request у
   *  OpenRouter. Тогда prompt и completion — нули, и это не «бесплатно». */
  request?: string;
  /** Цена длинного запроса «Про». */
  request_long?: string;
}

/** Модель в ответе `GET /llm/models`. */
export interface ModelIzAPI {
  id: string;
  object?: string;
  owned_by?: string;
  /**
   * Блок цен. Может отсутствовать, и это не сбой разбора: API опускает
   * его целиком, когда цена модели неизвестна (наши ai-saga-*) или
   * конвертация недоступна. Показать вместо этого «0» значило бы
   * назвать платную модель бесплатной.
   */
  pricing?: CenaModeli;
  context_length?: number;
  architecture?: {
    modality?: string;
    input_modalities?: string[];
    output_modalities?: string[];
  };
  /** Бесплатный суточный объём по ключу (ai-saga-lite). */
  free_tier?: { tokens_per_day?: number; max_message_chars?: number };
  /** Задачи «Про» (ai-saga-pro). */
  tasks?: string[];
}

/** Ответ ручки каталога. */
export interface OtvetKataloga {
  object?: string;
  data: ModelIzAPI[];
}

/** Строка таблицы: всё уже посчитано и пригодно для сравнения. */
export interface StrokaModeli {
  id: string;
  /** Имя поставщика, выделенное из идентификатора: `anthropic/...`. */
  postavshchik: string;
  /** Цена за миллион входных токенов. `null` — цена неизвестна. */
  vhod: number | null;
  /** Цена за миллион выходных токенов. */
  vyhod: number | null;
  valyuta: string;
  /** Размер контекста в токенах. Ноль — неизвестен. */
  kontekst: number;
  /** Понимает картинки. */
  kartinki: boolean;
  /** Принимает или выдаёт звук. */
  zvuk: boolean;
  /** Цена известна и равна нулю — модель действительно бесплатна.
   *  Или у неё есть бесплатный суточный объём (ai-saga-lite). */
  besplatnaya: boolean;
  /** Цена за запрос (ai-saga-pro). `null` — цена за токены, как у всех. */
  zaZapros: number | null;
  /** Цена длинного запроса. */
  zaDlinnyjZapros: number | null;
  /** Бесплатных токенов в сутки по ключу. `null` — бесплатного объёма нет. */
  besplatnoVSutki: number | null;
}

/**
 * Приставки маршрута в имени модели. Наружу модель показывается без неё
 * (решение владельца 15.10.2026): or/deepseek/deepseek-v3.2 →
 * deepseek/deepseek-v3.2. API делает то же сам (AI-Tech-API,
 * internal/domain/modelid); здесь — запасной путь, пока на проде старый
 * API, и правило обязано совпадать с ним.
 */
const PRISTAVKI = ['or/~', 'or/', 'openrouter/'];

/** Публичное имя модели: без приставки маршрута и без тильды. Снимается
 *  одна приставка: у or/openrouter/free openrouter/ — часть имени. */
export function publichnyjId(id: string): string {
  let ostatok = id;
  for (const p of PRISTAVKI) {
    if (ostatok.startsWith(p)) {
      ostatok = ostatok.slice(p.length);
      break;
    }
  }
  if (ostatok.startsWith('~')) ostatok = ostatok.slice(1);
  return ostatok === '' ? id : ostatok;
}

/** Старшинство при совпадении публичных имён: прямое имя, затем or/,
 *  or/~, openrouter/ — как в API. */
function starshinstvo(id: string): number {
  if (id.startsWith('or/~') || id.startsWith('~')) return 2;
  if (id.startsWith('or/')) return 1;
  if (id.startsWith('openrouter/')) return 3;
  return 0;
}

/**
 * Служебные модели: в каталоге API их больше нет, а старый API ещё
 * отдаёт. AI-TECH-FREE — модель старого ключа; openrouter/google/gemma-4-31b-it
 * — внутренний кандидат ai-saga-lite, дубль or/google/gemma-4-31b-it;
 * or/openrouter/free — маршрутизатор на случайную бесплатную модель;
 * voyage-* — эмбеддинги базы знаний, не чат.
 */
const SLUZHEBNYE = new Set([
  'AI-TECH-FREE',
  'openrouter/google/gemma-4-31b-it',
  'or/openrouter/free',
]);

export function sluzhebnayaModel(id: string): boolean {
  return SLUZHEBNYE.has(id) || id.startsWith('voyage-');
}

/**
 * Публичные имена для списка моделей. Совпало публичное имя у двух —
 * его получает старшая, остальные остаются под своим настоящим именем:
 * два одинаковых id в таблице хуже, чем одна приставка.
 */
export function publichnyeIdy(idy: string[]): Map<string, string> {
  const gruppy = new Map<string, string[]>();
  for (const id of idy) {
    const pub = publichnyjId(id);
    gruppy.set(pub, [...(gruppy.get(pub) ?? []), id]);
  }
  const itog = new Map<string, string>();
  for (const [pub, chleny] of gruppy) {
    const poStarshinstvu = [...chleny].sort(
      (a, b) => starshinstvo(a) - starshinstvo(b) || a.localeCompare(b),
    );
    poStarshinstvu.forEach((id, i) => itog.set(id, i === 0 ? pub : id));
  }
  return itog;
}

/** Сколько токенов в «миллионе»: цены принято показывать за 1M, а API
 *  отдаёт за один токен. */
const MILLION = 1_000_000;

/** Размер страницы таблицы. */
export const MODELEJ_NA_STRANICE = 25;

/**
 * Поставщик по идентификатору модели.
 *
 * `or/anthropic/claude-opus-5` — это OpenRouter поверх Anthropic, и
 * человеку важнее второе: выбирают по имени семейства. Поэтому у
 * трёхчастного имени берётся середина, а не первый сегмент. Поле
 * `owned_by` для этого не годится: прод отдаёт в нём `openai` у всех
 * 284 моделей подряд, включая claude и gemini. Проверено живым
 * запросом, не документацией.
 */
export function postavshchikModeli(id: string): string {
  const chasti = id.split('/');
  if (chasti.length >= 3) return chasti[1] ?? id;
  if (chasti.length === 2) return chasti[0] ?? id;
  // Имя без слэшей: либо наше собственное (ai-saga-lite), либо
  // openai-подобное (gpt-4o). Первое слово до дефиса — лучшее, что о
  // семействе можно сказать, не выдумывая.
  return id.split('-')[0] ?? id;
}

/** Цена за миллион токенов из строки за один токен. */
function zaMillion(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const chislo = Number(raw);
  if (!Number.isFinite(chislo) || chislo < 0) return null;
  return chislo * MILLION;
}

/** Переводит ответ API в строки таблицы. */
export function razobratModeli(otvet: OtvetKataloga | undefined): StrokaModeli[] {
  const spisok = otvet?.data;
  if (!Array.isArray(spisok)) return [];

  const vidimye = spisok.filter(
    (m) => typeof m.id === 'string' && m.id !== '' && !sluzhebnayaModel(m.id),
  );
  const idy = publichnyeIdy(vidimye.map((m) => m.id));

  return vidimye.map((m) => {
    const id = idy.get(m.id) ?? m.id;
    const zaZapros = chislo(m.pricing?.request);
    // У модели с ценой за запрос нули за токены — не «бесплатно» и не
    // цена: в колонках за миллион у неё прочерк.
    const vhod = zaZapros === null ? zaMillion(m.pricing?.prompt) : null;
    const vyhod = zaZapros === null ? zaMillion(m.pricing?.completion) : null;
    const vhodnye = m.architecture?.input_modalities ?? [];
    const vyhodnye = m.architecture?.output_modalities ?? [];
    const vSutki = m.free_tier?.tokens_per_day;
    const besplatnoVSutki = typeof vSutki === 'number' && vSutki > 0 ? vSutki : null;

    return {
      id,
      postavshchik: postavshchikModeli(id),
      vhod,
      vyhod,
      valyuta: m.pricing?.currency ?? '',
      kontekst: typeof m.context_length === 'number' ? m.context_length : 0,
      kartinki: vhodnye.includes('image'),
      zvuk: vhodnye.includes('audio') || vyhodnye.includes('audio'),
      // Ноль как цена засчитывается за «бесплатно» только когда блок
      // цен вообще пришёл: отсутствие цены — это «неизвестно», и
      // путать эти два состояния нельзя.
      besplatnaya: (vhod === 0 && vyhod === 0) || besplatnoVSutki !== null,
      zaZapros,
      zaDlinnyjZapros: zaZapros === null ? null : chislo(m.pricing?.request_long),
      besplatnoVSutki,
    };
  });
}

/** Число из строки цены; `null` — нет или не число. Ноль за запрос —
 *  не цена за запрос (OpenRouter пишет "0" у всех моделей). */
function chislo(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const v = Number(raw);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** По каким колонкам можно сортировать. */
export const POLYA_SORTIROVKI = ['id', 'vhod', 'vyhod', 'kontekst'] as const;
export type PoleSortirovki = (typeof POLYA_SORTIROVKI)[number];

/** Состояние таблицы. */
export interface ZaprosModelej {
  poisk: string;
  /** Пустой список означает «любой поставщик», а не «ни одного». */
  postavshchiki: string[];
  /** Показывать только бесплатные. */
  tolkoBesplatnye: boolean;
  /** Показывать только понимающие картинки. */
  tolkoKartinki: boolean;
  sort: PoleSortirovki;
  ubyvanie: boolean;
  stranica: number;
}

export const PUSTOJ_ZAPROS: ZaprosModelej = {
  poisk: '',
  postavshchiki: [],
  tolkoBesplatnye: false,
  tolkoKartinki: false,
  sort: 'id',
  ubyvanie: false,
  stranica: 1,
};

/** Заданы ли фильтры: пустая выдача при заданных и при пустых фильтрах
 *  объясняется человеку разными словами. */
export function estFiltry(z: ZaprosModelej): boolean {
  return z.poisk !== '' || z.postavshchiki.length > 0 || z.tolkoBesplatnye || z.tolkoKartinki;
}

/** Отбор по строке поиска и фильтрам. */
export function otobratModeli(modeli: StrokaModeli[], z: ZaprosModelej): StrokaModeli[] {
  const iskomoe = z.poisk.trim().toLowerCase();

  return modeli.filter((m) => {
    if (iskomoe !== '' && !m.id.toLowerCase().includes(iskomoe)) return false;
    if (z.postavshchiki.length > 0 && !z.postavshchiki.includes(m.postavshchik)) return false;
    if (z.tolkoBesplatnye && !m.besplatnaya) return false;
    if (z.tolkoKartinki && !m.kartinki) return false;
    return true;
  });
}

/**
 * Сортировка.
 *
 * Модели с неизвестным значением всегда уходят в конец, независимо от
 * направления. Иначе сортировка «сначала дешёвые» ставила бы первыми
 * те, про чью цену мы ничего не знаем, и таблица отвечала бы не на тот
 * вопрос, который задали.
 *
 * «Неизвестно» — это `null` у цены и ноль у контекста, но не ноль у
 * цены: бесплатная модель стоит ровно ноль, это настоящее значение, и
 * в списке «сначала дешёвые» ей место первой. Сначала было написано
 * одно правило на оба поля, и бесплатные уезжали в самый низ — поймал
 * тест, не экран.
 */
export function otsortirovatModeli(
  modeli: StrokaModeli[],
  sort: PoleSortirovki,
  ubyvanie: boolean,
): StrokaModeli[] {
  const znak = ubyvanie ? -1 : 1;

  return [...modeli].sort((a, b) => {
    if (sort === 'id') return znak * a.id.localeCompare(b.id);

    const x = sort === 'kontekst' ? (a.kontekst === 0 ? null : a.kontekst) : a[sort];
    const y = sort === 'kontekst' ? (b.kontekst === 0 ? null : b.kontekst) : b[sort];

    if (x === null && y === null) return a.id.localeCompare(b.id);
    if (x === null) return 1;
    if (y === null) return -1;

    return znak * (x - y);
  });
}

/** Страница списка. Номер приводится к существующему: фильтры могли
 *  сократить выдачу, пока человек стоял на десятой странице. */
export function stranicaModelej(
  modeli: StrokaModeli[],
  stranica: number,
  razmer = MODELEJ_NA_STRANICE,
): { strok: StrokaModeli[]; stranica: number; vsego: number; stranic: number } {
  const stranic = Math.max(1, Math.ceil(modeli.length / razmer));
  const nomer = Math.min(Math.max(1, stranica), stranic);
  const nachalo = (nomer - 1) * razmer;

  return {
    strok: modeli.slice(nachalo, nachalo + razmer),
    stranica: nomer,
    vsego: modeli.length,
    stranic,
  };
}

/** Список поставщиков с числом моделей, для фильтра. */
export function postavshchiki(modeli: StrokaModeli[]): { imya: string; skolko: number }[] {
  const schet = new Map<string, number>();
  for (const m of modeli) schet.set(m.postavshchik, (schet.get(m.postavshchik) ?? 0) + 1);

  return [...schet.entries()]
    .map(([imya, skolko]) => ({ imya, skolko }))
    .sort((a, b) => b.skolko - a.skolko || a.imya.localeCompare(b.imya));
}

/** Есть ли в каталоге хоть одна цена.
 *
 *  Прод сейчас отдаёт каталог без блока pricing целиком, и таблица с
 *  пустыми колонками цен выглядела бы сломанной. Признак позволяет
 *  честно сказать «цены сейчас недоступны» вместо ряда прочерков. */
export function estCeny(modeli: StrokaModeli[]): boolean {
  return modeli.some((m) => m.vhod !== null || m.vyhod !== null);
}

/**
 * Цена за миллион токенов человеческим видом.
 *
 * Дешёвые модели стоят доли цента за миллион, дорогие — сотни. Один
 * формат на оба случая даёт либо «0.00», либо «216.000000», поэтому
 * число знаков зависит от величины.
 */
export function pokazatCenu(v: number | null, valyuta: string, locale: string): string {
  if (v === null) return '—';
  if (v === 0) return '0';

  const znakov = v < 1 ? 4 : 2;
  const chislo = new Intl.NumberFormat(locale, {
    minimumFractionDigits: znakov,
    maximumFractionDigits: znakov,
  }).format(v);

  return valyuta === '' ? chislo : `${chislo} ${simvolValyuty(valyuta)}`;
}

/** Знак валюты по коду ISO. Неизвестный код показывается как есть:
 *  выдумать не тот символ хуже, чем напечатать три буквы кода. */
export function simvolValyuty(kod: string): string {
  switch (kod) {
    case 'RUB':
      return '\u20bd';
    case 'USD':
      return '$';
    case 'EUR':
      return '\u20ac';
    default:
      return kod;
  }
}

/** Размер контекста коротко: 128000 читается хуже, чем 128K. */
export function pokazatKontekst(t: number): string {
  if (t <= 0) return '—';
  if (t >= MILLION) return `${String(Math.round(t / MILLION))}M`;
  if (t >= 1000) return `${String(Math.round(t / 1000))}K`;
  return String(t);
}
